/**
 * The Pro editor's small vocabulary (design_handoff_pro_setup/README.md "Sections" + "Design
 * tokens"): white cards, 10px/700/.16em field labels, pill inputs on #F5F5F5 with a 2px black
 * focus ring, dashed add pills, the amber note, the animated rainbow ring. The club kit
 * (components/club/clubUi) supplies the switch, pill buttons, tag pills and the toast; this file
 * only adds what the editor needs on top.
 */
import React from 'react';
import { INK, MUTED, SUBTLE, INSET, CHIP, RULE, ringOn } from '../../club/clubUi';

export const AMBER_BG = '#FEF3C7';
export const AMBER_TEXT = '#78350F';
export const AMBER_TAG = '#92400E';
export const PRO_MAGENTA = '#FF00FF';
export const FLASH = '#FF00AA';

export const fieldLabel = { fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' };
export const cardTitle = { fontSize: 12, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' };
export const metaTag = { fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: SUBTLE, textTransform: 'uppercase' };
export const help = { fontSize: 12, lineHeight: 1.6, color: MUTED };

/** Pill input: 50 (default), 46 (dense) or 40 (inline) tall. */
export const pillInput = (h = 50, extra = {}) => ({
  width: '100%', height: h, padding: `0 ${h >= 50 ? 20 : h >= 46 ? 18 : 14}px`, border: 'none', borderRadius: 999, background: INSET,
  fontFamily: 'inherit', fontSize: h >= 46 ? 14 : 13, color: INK, boxSizing: 'border-box', outline: 'none', ...extra,
});
export const pillArea = (extra = {}) => ({
  width: '100%', padding: '14px 20px', border: 'none', borderRadius: 20, background: INSET, fontFamily: 'inherit', fontSize: 14, lineHeight: 1.55, color: INK, boxSizing: 'border-box', resize: 'vertical', outline: 'none', ...extra,
});

/** A white card. */
export function Card({ children, phone = false, style, ...rest }) {
  return (
    <section className="flex min-w-0 flex-col font-mono" style={{ background: '#fff', borderRadius: phone ? 22 : 24, padding: phone ? '18px 16px' : '22px 24px', gap: 14, color: INK, ...style }} {...rest}>
      {children}
    </section>
  );
}

export function CardHead({ title, right, children, style }) {
  return (
    <span className="flex flex-wrap items-center justify-between" style={{ gap: '8px 12px', ...style }}>
      <span style={cardTitle}>{title}</span>
      {right != null && <span style={metaTag}>{right}</span>}
      {children}
    </span>
  );
}

/** Label + control stacked, 8px apart. `right` sits opposite the label (a counter, a tag). */
export function Field({ label, right, hint, children, as: As = 'label', style, testId }) {
  return (
    <As className="flex min-w-0 flex-col" style={{ gap: 8, ...style }} data-testid={testId}>
      <span className="flex justify-between" style={{ gap: 10 }}><span style={fieldLabel}>{label}</span>{right}</span>
      {children}
      {hint && <span style={{ fontSize: 9, letterSpacing: '.12em', color: SUBTLE, textTransform: 'uppercase' }}>{hint}</span>}
    </As>
  );
}

export function TextInput({ h = 50, style, ...rest }) {
  return <input className="pr-pill-input font-mono" style={pillInput(h, style)} {...rest} />;
}

export function TextArea({ style, rows = 3, ...rest }) {
  return <textarea className="pr-pill-input font-mono" rows={rows} style={pillArea(style)} {...rest} />;
}

/** `+ ADD …` dashed pill. */
export function DashedButton({ children, h = 40, style, ...rest }) {
  return (
    <button type="button" className="font-mono hover:bg-gray-50" style={{ alignSelf: 'flex-start', height: h, padding: '0 18px', border: '2px dashed #000', borderRadius: 999, background: 'transparent', fontSize: 10, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', cursor: 'pointer', boxSizing: 'border-box', ...style }} {...rest}>
      {children}
    </button>
  );
}

/** 32px round ✕. */
export function RoundX({ size = 32, bg = CHIP, color = INK, style, ...rest }) {
  return <button type="button" className="font-mono" style={{ flexShrink: 0, width: size, height: size, border: 'none', borderRadius: '50%', background: bg, color, fontSize: 12, fontWeight: 800, cursor: 'pointer', ...style }} {...rest}>✕</button>;
}

/** Small on/off pill chip (attributes, marks, days). */
export function Chip({ on, children, h = 42, style, ...rest }) {
  return (
    <button type="button" className="font-mono" aria-pressed={!!on} style={{ height: h, padding: '0 18px', border: 'none', borderRadius: 999, background: on ? INK : CHIP, color: on ? '#fff' : INK, fontSize: 10, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer', transition: 'background-color .2s, color .2s', ...style }} {...rest}>
      {children}
    </button>
  );
}

/** Amber note (blockers, "pick a role", pending kind switch). */
export function AmberNote({ children, style, testId }) {
  return <div className="flex flex-col font-mono" style={{ gap: 4, padding: '12px 18px', borderRadius: 18, background: AMBER_BG, fontSize: 12, lineHeight: 1.55, color: AMBER_TEXT, ...style }} data-testid={testId}>{children}</div>;
}

/** Grey inset callout. */
export function Inset({ children, style, ...rest }) {
  return <div className="flex flex-col font-mono" style={{ gap: 8, padding: '14px 18px', borderRadius: 18, background: INSET, fontSize: 12, lineHeight: 1.6, color: MUTED, ...style }} {...rest}>{children}</div>;
}

/** The black pill with the moving rainbow ring (current chip, PUBLISH, SAVE CHANGES). */
export const ringPill = (on) => (on ? { background: ringOn, color: '#fff', border: '2px solid transparent' } : {});
export const ringClass = (on) => (on ? 'pr-ring-anim' : '');

export const rule = (i) => (i ? RULE : 'none');
export const faint = SUBTLE;
